// Voice Command Integration Hook
// React hook for voice commands with gesture control support

import { useState, useEffect, useCallback, useRef } from 'react';
import { wsService } from '../services/websocket.service';

interface VoiceCommandState {
  isListening: boolean;
  isProcessing: boolean;
  lastTranscript: string;
  lastCommand: string;
  confidence: number;
  error: string | null;
  availableCommands: VoiceCommandDefinition[];
  isSupported: boolean;
}

interface VoiceCommandDefinition {
  id: string;
  phrase: string;
  action: string;
  parameters?: Record<string, any>;
  description: string;
  category: 'navigation' | 'tab_management' | 'search' | 'collaboration' | 'general';
}

interface VoiceCommandResult {
  success: boolean;
  transcript: string;
  command: string;
  action: string;
  confidence: number;
  result?: any;
  error?: string;
}

// Default voice commands
const defaultCommands: VoiceCommandDefinition[] = [
  { id: '1', phrase: 'open new tab', action: 'open_tab', description: 'Open a new browser tab', category: 'tab_management' },
  { id: '2', phrase: 'close tab', action: 'close_tab', description: 'Close the current tab', category: 'tab_management' },
  { id: '3', phrase: 'search for', action: 'search', description: 'Search for something', category: 'search' },
  { id: '4', phrase: 'go to', action: 'navigate', description: 'Navigate to a URL', category: 'navigation' },
  { id: '5', phrase: 'organize tabs', action: 'organize', description: 'Organize open tabs', category: 'tab_management' },
  { id: '6', phrase: 'start collaboration', action: 'collab_start', description: 'Start a collaboration session', category: 'collaboration' },
  { id: '7', phrase: 'share tab', action: 'share', description: 'Share current tab', category: 'collaboration' },
  { id: '8', phrase: 'mute', action: 'mute', description: 'Mute notifications', category: 'general' },
  { id: '9', phrase: 'take screenshot', action: 'screenshot', description: 'Take a screenshot', category: 'general' },
  { id: '10', phrase: 'summarize', action: 'summarize', description: 'Summarize current page', category: 'general' },
];

function speechRecognitionSupported(): boolean {
  return typeof window !== 'undefined'
    && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);
}

function friendlySpeechError(code: string): string {
  switch (code) {
    case 'not-allowed':
      return 'Microphone access denied. Allow microphone permission in your browser settings.';
    case 'no-speech':
      return 'No speech detected. Try speaking again, closer to the microphone.';
    case 'audio-capture':
      return 'No microphone found. Connect a microphone or type your command below.';
    case 'network':
      return 'Speech recognition needs an internet connection (Chrome uses cloud transcription).';
    case 'aborted':
      return 'Listening was interrupted. Tap the microphone to try again.';
    case 'service-not-allowed':
      return 'Speech recognition is blocked on this page. Use HTTPS or type your command below.';
    default:
      return code || 'Voice recognition failed';
  }
}

export function useVoiceCommand(options?: {
  autoStart?: boolean;
  language?: string;
  onCommand?: (result: VoiceCommandResult) => void;
  onTranscript?: (transcript: string, confidence: number) => void | Promise<void>;
  onError?: (error: string) => void;
  commands?: VoiceCommandDefinition[];
}) {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [state, setState] = useState<VoiceCommandState>({
    isListening: false,
    isProcessing: false,
    lastTranscript: '',
    lastCommand: '',
    confidence: 0,
    error: null,
    availableCommands: options?.commands || defaultCommands,
    isSupported: speechRecognitionSupported(),
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const listeningRef = useRef(false);

  const processCommand = useCallback((transcript: string, confidence: number) => {
    const opts = optionsRef.current;
    const lowerTranscript = transcript.toLowerCase().trim();

    const matchedCommand = state.availableCommands.find(cmd =>
      lowerTranscript.includes(cmd.phrase.toLowerCase()) ||
      cmd.phrase.toLowerCase().includes(lowerTranscript)
    );

    const result: VoiceCommandResult = {
      success: !!matchedCommand,
      transcript,
      command: matchedCommand?.phrase || '',
      action: matchedCommand?.action || 'unknown',
      confidence,
      result: matchedCommand ? { action: matchedCommand.action, parameters: matchedCommand.parameters } : undefined,
      error: matchedCommand ? undefined : 'Command not recognized',
    };

    setState(prev => ({
      ...prev,
      isProcessing: false,
      lastTranscript: transcript,
      lastCommand: matchedCommand?.phrase || '',
      confidence,
      error: result.error || null,
    }));

    opts?.onCommand?.(result);

    if (wsService.isConnected()) {
      wsService.sendVoiceCommand({
        userId: 'current-user',
        transcript,
        command: result.command,
        confidence,
        action: result.action,
      });
    }

    return result;
  }, [state.availableCommands]);

  // Initialize speech recognition once; callbacks read from optionsRef.
  useEffect(() => {
    if (!speechRecognitionSupported()) {
      setState(prev => ({ ...prev, isSupported: false }));
      return;
    }

    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) return;

    const recognition = new Ctor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = optionsRef.current?.language || 'en-US';

    recognition.onresult = async (event: SpeechRecognitionEvent) => {
      const resultIndex = event.results.length - 1;
      const result = event.results[resultIndex];
      if (!result?.isFinal) return;

      const transcript = result[0]?.transcript?.trim() ?? '';
      const confidence = result[0]?.confidence ?? 0.9;
      if (!transcript) return;

      const opts = optionsRef.current;
      if (opts?.onTranscript) {
        setState(prev => ({
          ...prev,
          isListening: false,
          isProcessing: true,
          lastTranscript: transcript,
          confidence,
          error: null,
        }));
        listeningRef.current = false;
        try {
          await opts.onTranscript(transcript, confidence);
        } finally {
          setState(prev => ({ ...prev, isProcessing: false }));
        }
        return;
      }
      processCommand(transcript, confidence);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const message = friendlySpeechError(event.error);
      listeningRef.current = false;
      setState(prev => ({ ...prev, error: message, isListening: false }));
      optionsRef.current?.onError?.(message);
    };

    recognition.onend = () => {
      listeningRef.current = false;
      setState(prev => ({ ...prev, isListening: false }));
    };

    recognitionRef.current = recognition;

    return () => {
      listeningRef.current = false;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.abort();
      } catch {
        // ignore
      }
      recognitionRef.current = null;
    };
  }, [processCommand]);

  useEffect(() => {
    if (recognitionRef.current && options?.language) {
      recognitionRef.current.lang = options.language;
    }
  }, [options?.language]);

  const startListening = useCallback(async () => {
    if (!recognitionRef.current) {
      const message = 'Speech recognition is not supported in this browser. Type your command below.';
      setState(prev => ({ ...prev, error: message }));
      optionsRef.current?.onError?.(message);
      return;
    }

    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
      }

      recognitionRef.current.lang = optionsRef.current?.language || 'en-US';

      if (listeningRef.current) {
        recognitionRef.current.stop();
      }

      recognitionRef.current.start();
      listeningRef.current = true;
      setState(prev => ({ ...prev, isListening: true, error: null }));
    } catch (error) {
      const domError = error as DOMException;
      if (domError?.name === 'InvalidStateError' && recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current.start();
          listeningRef.current = true;
          setState(prev => ({ ...prev, isListening: true, error: null }));
          return;
        } catch {
          // fall through
        }
      }

      const message = domError?.name === 'NotAllowedError'
        ? friendlySpeechError('not-allowed')
        : 'Failed to start voice recognition';
      console.error('Failed to start listening:', error);
      setState(prev => ({ ...prev, error: message, isListening: false }));
      optionsRef.current?.onError?.(message);
    }
  }, []);

  const stopListening = useCallback(() => {
    try {
      if (recognitionRef.current && listeningRef.current) {
        recognitionRef.current.stop();
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
      listeningRef.current = false;
      setState(prev => ({ ...prev, isListening: false }));
    } catch (error) {
      console.error('Failed to stop listening:', error);
    }
  }, []);

  const toggleListening = useCallback(() => {
    if (listeningRef.current) {
      stopListening();
    } else {
      void startListening();
    }
  }, [startListening, stopListening]);

  const addCustomCommand = useCallback((command: VoiceCommandDefinition) => {
    setState(prev => ({
      ...prev,
      availableCommands: [...prev.availableCommands, command],
    }));
  }, []);

  const removeCommand = useCallback((commandId: string) => {
    setState(prev => ({
      ...prev,
      availableCommands: prev.availableCommands.filter(cmd => cmd.id !== commandId),
    }));
  }, []);

  const processAudioCommand = useCallback(async (audioBlob: Blob): Promise<VoiceCommandResult> => {
    setState(prev => ({ ...prev, isProcessing: true }));

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const result: VoiceCommandResult = {
        success: true,
        transcript: 'Simulated voice command',
        command: 'Voice command',
        action: 'process_audio',
        confidence: 0.9,
      };

      setState(prev => ({
        ...prev,
        isProcessing: false,
        lastTranscript: result.transcript,
        lastCommand: result.command,
        confidence: result.confidence,
      }));

      return result;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Audio processing failed';
      setState(prev => ({ ...prev, isProcessing: false, error: message }));
      return {
        success: false,
        transcript: '',
        command: '',
        action: 'unknown',
        confidence: 0,
        error: message,
      };
    }
  }, []);

  return {
    ...state,
    startListening,
    stopListening,
    toggleListening,
    addCustomCommand,
    removeCommand,
    processAudioCommand,
  };
}

// Gesture Control Hook
interface GestureState {
  isEnabled: boolean;
  currentGesture: string | null;
  confidence: number;
  lastGestureTime: Date | null;
}

interface GestureDefinition {
  id: string;
  name: string;
  description: string;
  action: string;
  parameters?: Record<string, any>;
}

const defaultGestures: GestureDefinition[] = [
  { id: '1', name: 'swipe_up', description: 'Swipe up to open new tab', action: 'open_tab' },
  { id: '2', name: 'swipe_down', description: 'Swipe down to close current tab', action: 'close_tab' },
  { id: '3', name: 'swipe_left', description: 'Swipe left to go back', action: 'navigate_back' },
  { id: '4', name: 'swipe_right', description: 'Swipe right to go forward', action: 'navigate_forward' },
  { id: '5', name: 'pinch_in', description: 'Pinch in to zoom out', action: 'zoom_out' },
  { id: '6', name: 'pinch_out', description: 'Pinch out to zoom in', action: 'zoom_in' },
  { id: '7', name: 'twofinger_tap', description: 'Two finger tap for menu', action: 'show_menu' },
];

export function useGestureControl(options?: {
  videoElement?: HTMLVideoElement | null;
  onGesture?: (gesture: GestureDefinition) => void;
  gestures?: GestureDefinition[];
}) {
  const [state, setState] = useState<GestureState>({
    isEnabled: false,
    currentGesture: null,
    confidence: 0,
    lastGestureTime: null,
  });

  const gestures = options?.gestures || defaultGestures;

  const enableGestures = useCallback(() => {
    setState(prev => ({ ...prev, isEnabled: true }));
  }, []);

  const disableGestures = useCallback(() => {
    setState(prev => ({ ...prev, isEnabled: false, currentGesture: null }));
  }, []);

  const detectGesture = useCallback((landmarks: any[]): GestureDefinition | null => {
    if (!landmarks || landmarks.length < 21) return null;

    const wrist = landmarks[0];
    const indexTip = landmarks[8];
    const middleTip = landmarks[12];

    if (wrist.y > indexTip.y && wrist.y > middleTip.y) {
      return gestures.find(g => g.id === '1') || null;
    }

    return null;
  }, [gestures]);

  const handleGesture = useCallback((gesture: GestureDefinition) => {
    setState(prev => ({
      ...prev,
      currentGesture: gesture.name,
      confidence: 0.9,
      lastGestureTime: new Date(),
    }));

    options?.onGesture?.(gesture);
  }, [options]);

  return {
    ...state,
    enableGestures,
    disableGestures,
    detectGesture,
    handleGesture,
    availableGestures: gestures,
  };
}

// Export all types
export type { VoiceCommandState, VoiceCommandDefinition, VoiceCommandResult, GestureState, GestureDefinition };
